// HealthQuestionnairesTemplate.tsx
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHealthQuestionnaireService } from "@/rtkQuery/features/hooks/useHealthQuestionnaireService";
import {
    Button,
    Typography,
    CheckboxOption,
    ShimmerEffect,
    LogoDark,
} from "@/design-system/Atoms";
import { AppErrorState, AppEmptyState } from "@/design-system/Feedbacks";
import { ProgressStepper } from "@/design-system/Molecules";
import { debouncedToastService } from "@/utils/debouncedToastService";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { ROUTES } from "@/routes/routes.config";
import { useNavigate } from "react-router-dom";
import MedicalSideEffectsAccordionSection from "@/design-system/Molecules/MedicalSideEffectsAccordionSection";

interface ResponseOption {
    Id: number;
    Label: string;
    MustStop: boolean;
    MustStopRedirectLink?: string;
    StopText?: string;
    HasOtherValue?: boolean;
    HasOptionalTextBox?: boolean;
}

interface Question {
    Id: number;
    Title: string;
    ResponseOptions: ResponseOption[];
    HealthQuestionType: number;
    MinSelectedAnswers: number;
    MaxSelectedAnswers: number;
    SortOrder: number;
}

interface QuestionnaireResponse {
    questionId: number;
    selectedOptions: string[];
    sortOrder: number;
    otherText?: string;
}

const HealthQuestionnairesTemplate = () => {
    const {
        healthQuestionnaireData = [],
        healthQuestionnaireIsLoading,
        healthQuestionnaireError,
        healthQuestionnaireRefetch,
        healthQuestionnaireIsSubmitting,
        submitHealthQuestionnaire,
    } = useHealthQuestionnaireService() as any;

    const { ref } = useInViewOnce<HTMLDivElement>();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(1);
    const [responses, setResponses] = useState<Record<number, QuestionnaireResponse>>({});
    const [mustStopOption, setMustStopOption] = useState<any>(null);
    const [showStopTextOption, setShowStopTextOption] = useState<any>(null);
    const [validationError, setValidationError] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const currentQuestion: Question = healthQuestionnaireData[currentStep - 1];
    const isLastStep = currentStep === healthQuestionnaireData.length;
    const isMultipleChoice = currentQuestion?.HealthQuestionType === 1;
    const isTextInput = currentQuestion?.HealthQuestionType === 3;

    useEffect(() => {
        if (currentStep > 1) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
    }, [currentStep]);

    const { watch, setValue, handleSubmit, reset, register, formState: { errors }, clearErrors } = useForm<{
        selected: string | string[];
        textInput?: string;
        otherText?: string;
    }>({
        defaultValues: {
            selected: isMultipleChoice ? [] : "",
            textInput: "",
            otherText: ""
        },
    });

    const selected = watch("selected");
    const textInput = watch("textInput");
    const otherText = watch("otherText");

    const handleToggle = (value: string) => {
        if (isMultipleChoice) {
            const selectedArray = Array.isArray(selected) ? selected : [];
            const isCurrentlySelected = selectedArray.includes(value);
            const lastOption = currentQuestion?.ResponseOptions?.[currentQuestion.ResponseOptions.length - 1];
            const isLastOption = lastOption && value === lastOption.Label;

            if (isCurrentlySelected) {
                const newSelected = selectedArray.filter(item => item !== value);
                setValue("selected", newSelected, { shouldValidate: true });
            } else {
                if (isLastOption) {
                    setValue("selected", [value], { shouldValidate: true });
                    setValue("otherText", "");
                    return;
                }

                let newSelected = selectedArray;
                if (lastOption && selectedArray.includes(lastOption.Label)) {
                    newSelected = selectedArray.filter(item => item !== lastOption.Label);
                }

                const maxAllowed = currentQuestion?.MaxSelectedAnswers || Infinity;
                if (newSelected.length >= maxAllowed) {
                    debouncedToastService.error(`You can select a maximum of ${maxAllowed} option${maxAllowed > 1 ? 's' : ''}.`, "max-options");
                    return;
                }

                newSelected = [...newSelected, value];
                setValue("selected", newSelected, { shouldValidate: true });
            }
        } else {
            setValue("selected", value === selected ? "" : value, { shouldValidate: true });
        }
    };

    const validateCurrentQuestion = () => {
        const minRequired = currentQuestion?.MinSelectedAnswers || 0;
        const maxAllowed = currentQuestion?.MaxSelectedAnswers || Infinity;

        setValidationError("");

        if (isTextInput) {
            const textValue = textInput?.trim() || "";
            if (minRequired > 0 && textValue === "") {
                setValidationError("Please provide an answer.");
                return false;
            }
            return true;
        } else if (isMultipleChoice) {
            const selectedArray = Array.isArray(selected) ? selected : [];

            const hasOtherValueSelected = selectedArray.some(selectedOption => {
                const option = currentQuestion?.ResponseOptions?.find(opt => opt.Label === selectedOption);
                return option?.HasOtherValue && (!otherText || otherText.trim() === "");
            });

            if (hasOtherValueSelected) {
                setValidationError("Please specify your answer for the selected option.");
                return false;
            }

            if (selectedArray.length < minRequired) {
                if (minRequired === 1) {
                    setValidationError("Please select at least one option.");
                } else {
                    setValidationError(`Please select at least ${minRequired} option${minRequired > 1 ? 's' : ''}.`);
                }
                return false;
            }

            if (selectedArray.length > maxAllowed) {
                setValidationError(`You can select a maximum of ${maxAllowed} option${maxAllowed > 1 ? 's' : ''}.`);
                return false;
            }

            return true;
        } else {
            const selectedValue = selected as string;

            if (selectedValue) {
                const option = currentQuestion?.ResponseOptions?.find(opt => opt.Label === selectedValue);
                if (option?.HasOtherValue && (!otherText || otherText.trim() === "")) {
                    setValidationError("Please specify your answer for the selected option.");
                    return false;
                }
            }

            if (minRequired > 0 && (!selectedValue || selectedValue.trim() === "")) {
                const isLastQuestion = currentStep === healthQuestionnaireData.length;
                const message = isLastQuestion
                    ? "Please acknowledge to continue"
                    : "Please select an answer.";
                setValidationError(message);
                return false;
            }

            return true;
        }
    };

    const buildPayload = () => {
        const payload: any = {
            isTestApproval: true,
            isTestPDE5: true,
            acknowledged: true,
        };

        const questionMapping: Record<number, string> = {
            1: "patientIsAdult",
            2: "difficultyAchievingErectionDuringSexualActivities",
            3: "difficultyAchievingErectionDuringMasterbation",
            4: "morningErections",
            5: "currentlyTakingErectileDysfunctionMedications",
            6: "patientHasAllergies",
            7: "listMedicalConditions",
            8: "medicationsMultipleChoice",
            9: "patientHasTriedPDE5MedicationsWithFailure",
            10: "patientHasTriedPDE5MedicationsUndesiredSideEffects",
            11: "patientToldNotHealthyEnoughForSex",
            12: "patientKnowsBloodPressure",
            13: "systolicBloodPressure",
            14: "diastolicBloodPressure",
            15: "patientPreviouslyDiagnosedLowBloodPressure",
            16: "patientPreviouslyDiagnosedHighBloodPressure",
            17: "patientHasHeartConditions",
            18: "patientExperiencesChestPainWhileActive",
            19: "patientExperiencesDizzinessOrFainting",
            20: "patientExperiencesBlurryVisionOrHeadaches",
            21: "conditionsMultipleChoice",
            22: "anythingForDefyMedical",
            23: "acknowledgementOfSideEffects",
        };

        Object.values(responses).forEach((response: QuestionnaireResponse) => {
            const fieldName = questionMapping[response.questionId];
            if (!fieldName) return;

            if (fieldName === "medicationsMultipleChoice") {
                payload.medicationsMultipleChoice = {
                    alphaBlockerMedications: response.selectedOptions.includes("Alpha-Blocker Medications (Doxazosin, Prazosin, Terazosin)"),
                    amylNitrate: response.selectedOptions.includes("Amyl Nitrate"),
                    noMedications: response.selectedOptions.includes("I do NOT take any of these medications"),
                    moreThanOneListedMedication: response.selectedOptions.includes("I take more than one of these medications"),
                    isosorbideDinitrate: response.selectedOptions.includes("Isosorbide Dinitrate"),
                    isosorbideMononitrate: response.selectedOptions.includes("Isosorbide Mononitrate"),
                    nitroglycerine: response.selectedOptions.includes("Nitroglycerine"),
                    poppers: response.selectedOptions.includes("\"Poppers\""),
                    riociguat: response.selectedOptions.includes("Riociguat"),
                    ritonavir: response.selectedOptions.includes("Ritonavir"),
                };
            } else if (fieldName === "conditionsMultipleChoice") {
                payload.conditionsMultipleChoice = {
                    sensitivityToPDE5Inhibitor: response.selectedOptions.includes("A known hypersensitivity to any PDE5 inhibitor, such as Viagra (sildenafil), Cialis (tadalafil), Levitra (vardenafil), Stendra (avanafil), etc"),
                    abnormalHeartbeat_CardiacArrhythmia_CongenitalQTProlongation: response.selectedOptions.includes("Abnormal heartbeat, cardiac arrhythmia, or congenital QT prolongation"),
                    aorticStenosis_IdiopathicHypertrophicSubaorticStenosis_HypertrophicObstructiveCardioMyopathy: response.selectedOptions.includes("Aortic stenosis, idiopathic hypertrophic subaortic stenosis, hypertrophic obstructive cardiomyopathy (HOCM)"),
                    currentOrActiveCancer: response.selectedOptions.includes("Current or Active Cancer"),
                    eyeDisorders: response.selectedOptions.includes("Eye disorders such as non-arteritic anterior ischemic optic neuropathy (NAION) or retinitis pigmentosa"),
                    previousHeartConditions: response.selectedOptions.includes("Heart Disease, Heart attack, chest pain, or angina"),
                    historyOfFaintingOrPassingOut: response.selectedOptions.includes("History of fainting or passing out"),
                    prolongedErection: response.selectedOptions.includes("History of prolonged erection longer than 4 hours (priapism)"),
                    kidneyProblems: response.selectedOptions.includes("Kidney problems – a medical provider has informed you that your kidneys are not functioning normally"),
                    liverProblems: response.selectedOptions.includes("Liver problems - a medical provider has informed you that your liver is not functioning normally"),
                    noConditions: response.selectedOptions.includes("No, I do NOT have any of these"),
                    scarringOrAbnormalities: response.selectedOptions.includes("Scarring or other physical abnormalities related to your penis including Peyronie's disease"),
                    sickleCellAnemia_MultipleMyeloma_Leukemia: response.selectedOptions.includes("Sickle cell anemia, multiple myeloma, or leukemia"),
                    stroke_TransientIschemicAttack: response.selectedOptions.includes("Stroke or Transient Ischemic Attack (TIA)"),
                };
            } else {
                payload[fieldName] = {
                    answer: response.selectedOptions[0] || "",
                    sortOrder: response.sortOrder,
                };
            }
        });

        const specialFields = [
            "difficultyAchievingErectionDuringMasterbation",
            "morningErections",
            "currentlyTakingErectileDysfunctionMedications",
            "patientHasAllergies",
            "patientPreviouslyDiagnosedHighBloodPressure"
        ];

        specialFields.forEach(field => {
            if (payload[field]) {
                payload[field] = {
                    answer: {
                        answer: payload[field].answer,
                        checked: true
                    }
                };
            }
        });

        return payload;
    };

    const onSubmit = async (data: any) => {
        if (isSubmitting) {
            return;
        }

        const questionId = currentQuestion?.Id;
        if (!questionId) return;

        if (!validateCurrentQuestion()) {
            return;
        }

        setIsSubmitting(true);

        try {
            let selectedOptions: string[] = [];
            let otherTextValue = "";

            if (isTextInput) {
                selectedOptions = [textInput || ""];
            } else if (isMultipleChoice) {
                selectedOptions = Array.isArray(data.selected) ? data.selected : [];
                otherTextValue = otherText || "";
            } else {
                selectedOptions = [data.selected || ""];
                otherTextValue = otherText || "";
            }

            const responseOptions = currentQuestion?.ResponseOptions || [];
            const stopOption = responseOptions.find(
                (opt: ResponseOption) => selectedOptions.includes(opt.Label) && opt.MustStop
            );

            if (stopOption) {
                setMustStopOption(stopOption);
                window.open("https://www.defymedical.com/get-started/", "_blank");
                return;
            }

            const newResponse: QuestionnaireResponse = {
                questionId,
                selectedOptions,
                sortOrder: currentQuestion.SortOrder,
                otherText: otherTextValue,
            };

            const updatedResponses = { ...responses, [questionId]: newResponse };
            setResponses(updatedResponses);

            if (!isLastStep) {
                setCurrentStep((prev) => prev + 1);
            } else {
                try {
                    const payload = buildPayload();
                    const response = await submitHealthQuestionnaire(payload);
                    if (response.Result === "Success") {
                        navigate(ROUTES.SELF_SCHEDULE)
                    }
                } catch (error) {
                    debouncedToastService.error("Failed to submit questionnaire. Please try again.", "submit-failure");
                    console.error("Submission error:", error);
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    useEffect(() => {
        const currentResponse = responses[currentQuestion?.Id];
        if (currentResponse) {
            if (isMultipleChoice) {
                reset({
                    selected: currentResponse.selectedOptions,
                    otherText: currentResponse.otherText || ""
                });
            } else if (isTextInput) {
                reset({
                    textInput: currentResponse.selectedOptions[0] || "",
                    otherText: currentResponse.otherText || ""
                });
            } else {
                reset({
                    selected: currentResponse.selectedOptions[0] || "",
                    otherText: currentResponse.otherText || ""
                });
            }
        } else {
            reset({
                selected: isMultipleChoice ? [] : "",
                textInput: "",
                otherText: ""
            });
        }
        setMustStopOption(null);
        setShowStopTextOption(null);
        setValidationError("");
    }, [currentStep, reset, responses, currentQuestion?.Id, isMultipleChoice, isTextInput]);

    useEffect(() => {
        if (!currentQuestion?.ResponseOptions || isTextInput) return;

        const responseOptions = currentQuestion.ResponseOptions;
        let stopOption = null;
        let showStopOption = null;

        if (isMultipleChoice) {
            const selectedArray = Array.isArray(selected) ? selected : [];
            stopOption = responseOptions.find(
                (opt: ResponseOption) => selectedArray.includes(opt.Label) && opt.MustStop
            );
            showStopOption = responseOptions.find(
                (opt: ResponseOption) => selectedArray.includes(opt.Label) && !opt.MustStop && opt.StopText
            );
        } else {
            stopOption = responseOptions.find(
                (opt: ResponseOption) => opt.Label === selected && opt.MustStop
            );
            showStopOption = responseOptions.find(
                (opt: ResponseOption) => opt.Label === selected && !opt.MustStop && opt.StopText
            );
        }

        setMustStopOption(stopOption || null);
        setShowStopTextOption(showStopOption || null);
    }, [selected, currentQuestion, isMultipleChoice, isTextInput]);

    useEffect(() => {
        if (validationError) {
            const minRequired = currentQuestion?.MinSelectedAnswers || 0;
            const maxAllowed = currentQuestion?.MaxSelectedAnswers || Infinity;

            if (isTextInput) {
                const textValue = textInput?.trim() || "";
                if (minRequired > 0 && textValue !== "") {
                    setValidationError("");
                }
            } else if (isMultipleChoice) {
                const selectedArray = Array.isArray(selected) ? selected : [];
                if (selectedArray.length >= minRequired && selectedArray.length <= maxAllowed) {
                    setValidationError("");
                }
            } else {
                const selectedValue = selected as string;
                if (minRequired > 0 && selectedValue && selectedValue.trim() !== "") {
                    setValidationError("");
                }
            }
        }
    }, [selected, textInput, validationError, currentQuestion, isTextInput, isMultipleChoice]);

    if (!healthQuestionnaireIsLoading && healthQuestionnaireError) {
        return <AppErrorState message="Failed to load questionnaire." onRetry={healthQuestionnaireRefetch} />;
    }

    if (!healthQuestionnaireIsLoading && !healthQuestionnaireData.length) {
        return <AppEmptyState message="No health questions found." />;
    }

    return (
        <div className="relative bg-[var(--color-bg-cream)] bg-[url('/images/bg-wizard.svg')] bg-no-repeat bg-cover bg-top overflow-hidden">
            <div className="pb-[20rem]">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col lg:flex-row">
                        <div className="lg:w-8/12 w-full mx-auto p-6 rounded-[20px]">
                            {healthQuestionnaireIsLoading ? (
                                <div className="w-full p-6 rounded-2xl mt-10 bg-white/70 shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur-md">
                                    <ShimmerEffect className="h-[80px] w-full mt-10 mb-6" />
                                    <ShimmerEffect className="h-[38px] w-[140px] my-6" />
                                    <ShimmerEffect className="h-[108px] w-full" />
                                    <div className="space-y-4">
                                        <ShimmerEffect className="h-[76px] w-full rounded-[8px] mt-10" />
                                        <ShimmerEffect className="h-[76px] w-full rounded-[8px]" />
                                    </div>
                                    <ShimmerEffect className="h-[66px] w-full mt-10" />
                                </div>
                            ) : (
                                <>
                                    <ProgressStepper currentStep={currentStep} totalSteps={healthQuestionnaireData.length} />
                                    <LogoDark className="mb-10" />
                                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                                        <div key={currentStep} className="animate-fade-in-up">
                                            <Typography variant="sectionHeading" className="mb-6">
                                                {currentQuestion?.Title}
                                            </Typography>
                                            {isTextInput ? (
                                                <div className="space-y-4">
                                                    <textarea
                                                        {...register("textInput", {
                                                            required: "This field is required",
                                                            validate: (value) => {
                                                                if (!value || value.trim() === "") {
                                                                    return "Please provide an answer";
                                                                }
                                                                return true;
                                                            }
                                                        })}
                                                        className="w-full p-4 border border-gray-300 rounded-md resize-none"
                                                        rows={4}
                                                        placeholder="Please provide your answer..."
                                                        value={textInput || ""}
                                                        onChange={(e) => {
                                                            setValue("textInput", e.target.value);
                                                            if (errors.textInput) {
                                                                clearErrors("textInput");
                                                            }
                                                            if (validationError && e.target.value.trim() !== "") {
                                                                setValidationError("");
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    {isMultipleChoice && (
                                                        <div className="mb-4 text-sm text-gray-600">
                                                            {currentQuestion?.MinSelectedAnswers > 0 && currentQuestion?.MaxSelectedAnswers < Infinity ? (
                                                                <span>Select {currentQuestion.MinSelectedAnswers} to {currentQuestion.MaxSelectedAnswers} options</span>
                                                            ) : currentQuestion?.MinSelectedAnswers > 0 ? (
                                                                <span>Select at least {currentQuestion.MinSelectedAnswers} option{currentQuestion.MinSelectedAnswers > 1 ? 's' : ''}</span>
                                                            ) : currentQuestion?.MaxSelectedAnswers < Infinity ? (
                                                                <span>Select up to {currentQuestion.MaxSelectedAnswers} option{currentQuestion.MaxSelectedAnswers > 1 ? 's' : ''}</span>
                                                            ) : null}
                                                            {Array.isArray(selected) && selected.length > 0 && (
                                                                <span className="ml-2 text-blue-600">
                                                                    ({selected.length} selected)
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {currentQuestion?.ResponseOptions?.map((option: ResponseOption) => (
                                                        <div key={option.Id}>
                                                            {isLastStep && (
                                                                <div className="mb-6">
                                                                    <MedicalSideEffectsAccordionSection />
                                                                </div>
                                                            )}

                                                            <CheckboxOption
                                                                id={`option-${option.Id}`}
                                                                label={option.Label}
                                                                checked={
                                                                    isMultipleChoice
                                                                        ? Array.isArray(selected) && selected.includes(option.Label)
                                                                        : selected === option.Label
                                                                }
                                                                onChange={() => handleToggle(option.Label)}
                                                            />

                                                            {option.HasOtherValue && (
                                                                (isMultipleChoice && Array.isArray(selected) && selected.includes(option.Label)) ||
                                                                (!isMultipleChoice && selected === option.Label)
                                                            ) && (
                                                                    <div className="mt-2 ml-6">
                                                                        <input
                                                                            {...register("otherText", {
                                                                                required: "Please specify your answer",
                                                                                validate: (value) => {
                                                                                    const isOptionSelected = isMultipleChoice
                                                                                        ? Array.isArray(selected) && selected.includes(option.Label)
                                                                                        : selected === option.Label;

                                                                                    if (isOptionSelected && (!value || value.trim() === "")) {
                                                                                        return "Please specify your answer";
                                                                                    }
                                                                                    return true;
                                                                                }
                                                                            })}
                                                                            type="text"
                                                                            className="w-full p-2 border border-gray-300 rounded-md"
                                                                            placeholder="Please specify..."
                                                                            value={otherText || ""}
                                                                            onChange={(e) => {
                                                                                setValue("otherText", e.target.value);
                                                                                if (errors.otherText) {
                                                                                    clearErrors("otherText");
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {validationError && (
                                                <div className="animate-fade-in-up mt-6 p-4 border border-red-300 bg-red-50 rounded-md text-sm text-red-700">
                                                    {validationError}
                                                </div>
                                            )}
                                            {(errors.textInput || errors.otherText) && (
                                                <div className="animate-fade-in-up mt-6 p-4 border border-red-300 bg-red-50 rounded-md text-sm text-red-700">
                                                    {errors.textInput?.message || errors.otherText?.message}
                                                </div>
                                            )}
                                            {mustStopOption?.StopText && (
                                                <div
                                                    ref={ref}
                                                    className="animate-fade-in-up mt-6 p-4 border border-red-300 bg-red-50 rounded-md text-sm text-red-700"
                                                    dangerouslySetInnerHTML={{ __html: mustStopOption.StopText }}
                                                />
                                            )}
                                            {showStopTextOption?.StopText && (
                                                <div
                                                    className="animate-fade-in-up mt-6 p-4 border border-yellow-300 bg-yellow-50 rounded-md text-sm text-yellow-800"
                                                    dangerouslySetInnerHTML={{ __html: showStopTextOption.StopText }}
                                                />
                                            )}
                                            <div className="flex flex-row items-center gap-6 mt-10">
                                                {currentStep > 1 && (
                                                    <Button
                                                        variant="darkSubmitButton"
                                                        size="xl"
                                                        className="!w-full"
                                                        onClick={handlePrevious}
                                                    >
                                                        <span className="text">Previous</span>
                                                    </Button>
                                                )}

                                                <Button
                                                    variant="secondary"
                                                    size="xl"
                                                    className="!w-full md:w-auto"
                                                    type="submit"
                                                    disabled={healthQuestionnaireIsSubmitting || isSubmitting}
                                                    loading={healthQuestionnaireIsSubmitting || isSubmitting}
                                                >
                                                    {isLastStep ? "Submit Questionnaire" : "Continue"}
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HealthQuestionnairesTemplate;